import { Link } from 'react-router-dom';
import { motion, useCycle, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'react-feather';
import { SITE_CONFIG } from './config';
import { useTheme } from './App';
import { useEffect } from 'react';

function Navbar() {
  const { isDarkMode, theme, toggleTheme } = useTheme();
  const [isOpen, toggleOpen] = useCycle(false, true);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const menuVariants = {
    open: {
      x: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    closed: {
      x: "100%",
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    },
  };

  const itemVariants = {
    open: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    closed: {
      y: -20,
      opacity: 0,
      transition: {
        duration: 0.2
      }
    }
  };

  const containerVariants = {
    open: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    },
    closed: {
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  };

  return (
    <nav className={`flex justify-between items-center p-4 md:p-6 ${theme.primary.navbar} border-b shadow-xl relative z-40`}>
      <div className="flex items-center">
        <Link to="/" className="flex items-center space-x-2 md:space-x-3">
          <img src={isDarkMode ? "/black_logo.png" : "/white_logo.png"} alt={SITE_CONFIG.brandName} className="w-8 h-8 md:w-12 md:h-12 object-contain" />
          <span className={`text-lg md:text-2xl font-bold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent`}>
            {SITE_CONFIG.brandName}
          </span>
        </Link>
      </div>
      <div className="flex items-center space-x-2 md:space-x-6">
        <button
          onClick={toggleTheme}
          className={`${theme.primary.card} ${theme.primary.text} px-2 py-1 md:px-4 md:py-2 rounded-full hover:${theme.primary.cardHover} transition-all duration-300 border shadow-lg text-sm md:text-base`}
        >
          {isDarkMode ? '☀️' : '🌙'}
        </button>
        <button
          onClick={() => toggleOpen()}
          className={`md:hidden ${theme.primary.text} p-2 ${theme.primary.card} rounded-lg border relative z-50`}
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        {/* Mobile Full-Screen Menu */}
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Fully Opaque Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-black z-[95] md:hidden"
                onClick={() => toggleOpen()}
              />

              {/* Slide-in Menu - Full Opacity Background */}
              <motion.div
                initial="closed"
                animate="open"
                exit="closed"
                variants={menuVariants}
                className={`fixed top-0 right-0 w-full h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-white'} z-[100] md:hidden flex flex-col overflow-hidden`}
              >
                {/* Menu Header with Logo - Solid Background */}
                <div className={`flex justify-between items-center p-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-center space-x-2">
                    <img src={isDarkMode ? "/black_logo.png" : "/white_logo.png"} alt={SITE_CONFIG.brandName} className="w-8 h-8 object-contain" />
                    <span className={`text-lg font-bold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent`}>
                      {SITE_CONFIG.brandName}
                    </span>
                  </div>
                  <button
                    onClick={() => toggleOpen()}
                    className={`${theme.primary.text} p-2 rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-300'} border`}
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Menu Items - Top-aligned with Solid Background */}
                <motion.div
                  variants={containerVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  className={`flex-1 flex flex-col justify-start items-center px-6 pt-12 pb-8 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
                >
                  <div className="w-full max-w-sm space-y-4">
                    {[
                      { to: '/', label: 'Home', emoji: '🏠' },
                      { to: '/deals', label: 'Deals', emoji: '🎯' },
                      { to: '/faq', label: 'FAQ', emoji: '❓' },
                      { to: '/contact', label: 'Contact', emoji: '📧' }
                    ].map((link) => (
                      <motion.div
                        key={link.to}
                        variants={itemVariants}
                        className="w-full"
                      >
                        <Link
                          to={link.to}
                          onClick={() => toggleOpen()}
                          className={`${theme.primary.text} flex items-center justify-center text-xl font-semibold transition-all duration-300 py-4 rounded-xl ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border hover:scale-[1.02] hover:shadow-lg`}
                        >
                          <span className="mr-3 text-2xl">{link.emoji}</span>
                          <span>{link.label}</span>
                        </Link>
                      </motion.div>
                    ))}

                    {/* Conditionally render Discord button in mobile menu */}
                    {SITE_CONFIG.display.navbar.discordButton && (
                      <>
                        {/* Divider */}
                        <motion.div
                          variants={itemVariants}
                          className={`w-full h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} my-6`}
                        />

                        {/* Discord Button */}
                        <motion.div
                          variants={itemVariants}
                          className="w-full"
                        >
                          <a
                            href={SITE_CONFIG.social.discord}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => toggleOpen()}
                            className={`bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-4 rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 flex items-center justify-center space-x-3 shadow-xl font-bold text-lg w-full hover:scale-[1.02]`}
                          >
                            <img src={'/Discord-Symbol-White.png'} alt="Discord" className="w-6 h-6 object-contain" />
                            <span>Join Community</span>
                          </a>
                        </motion.div>
                      </>
                    )}
                  </div>
                </motion.div>

                {/* Footer - Solid Background */}
                <div className={`p-4 text-center ${theme.primary.textMuted} text-sm border-t ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                  Shop Smart, Save More
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center space-x-6">
          {[
            { to: '/', label: 'Home' },
            { to: '/deals', label: 'Deals' },
            { to: '/faq', label: 'FAQ' },
            { to: '/contact', label: 'Contact' }
          ].map((link) => (
            <motion.div key={link.to} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to={link.to}
                className={`${theme.primary.text} hover:bg-gradient-to-r hover:${theme.primary.accent} hover:bg-clip-text hover:text-transparent font-medium transition-colors`}
              >
                {link.label}
              </Link>
            </motion.div>
          ))}
          {/* Conditionally render Discord button in desktop menu */}
          {SITE_CONFIG.display.navbar.discordButton && (
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <a
                href={SITE_CONFIG.social.discord}
                target="_blank"
                rel="noopener noreferrer"
                className={`bg-gradient-to-r ${theme.primary.button} text-white px-6 py-3 rounded-full hover:${theme.primary.buttonHover} transition-all duration-300 flex items-center space-x-2 shadow-xl border border-white/20`}
              >
                <img src={'/Discord-Symbol-White.png'} alt="Discord" className="w-6 h-6 object-contain" />
                <span>Join the FREE Server</span>
              </a>
            </motion.div>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
